import { useState } from 'react'

const catalogApps = [
  {
    appName: 'terminal',
    name: 'Terminal',
    category: 'Applications',
    description: 'Command line shell with themes and repository inspection',
    icon: 'fas fa-terminal',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/20'
  },
  {
    appName: 'crumb',
    name: 'Crumb AI Assistant',
    category: 'AI Tools',
    description: 'Sentient cupcake assistant with streaming chat and personality',
    icon: 'fas fa-robot',
    color: 'text-pink-400',
    bg: 'bg-pink-500/20'
  },
  {
    appName: 'browser',
    name: 'Web Browser',
    category: 'Applications',
    description: 'Multi-tab web browser and project gateway with history controls',
    icon: 'fas fa-globe',
    color: 'text-blue-400',
    bg: 'bg-blue-500/20'
  },
  {
    appName: 'tetris',
    name: 'Tetris',
    category: 'Games',
    description: 'Classic block stacking puzzle game with speed scaling',
    icon: 'fas fa-cubes',
    color: 'text-purple-400',
    bg: 'bg-purple-500/20'
  },
  {
    appName: 'snake',
    name: 'Snake',
    category: 'Games',
    description: 'Retro arcade snake with direction controls and high scores',
    icon: 'fas fa-dragon',
    color: 'text-green-400',
    bg: 'bg-green-500/20'
  },
  {
    appName: 'arkanoid',
    name: 'Arkanoid',
    category: 'Games',
    description: 'Retro arcade brick breaker featuring 12 rounds, power capsules, and boss DOH',
    icon: 'fas fa-table-tennis-paddle-ball',
    color: 'text-amber-400',
    bg: 'bg-amber-500/20'
  },
  {
    appName: 'chess',
    name: 'Chess',
    category: 'Games',
    description: 'FIDE board game with legal move validation, Minimax AI, and move history',
    icon: 'fas fa-chess',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/20'
  },
  {
    appName: 'spaceinvaders',
    name: 'Space Invaders',
    category: 'Games',
    description: 'Retro arcade alien invasion shooter with destructible shields, UFO, and 8-bit audio',
    icon: 'fas fa-space-shuttle',
    color: 'text-blue-400',
    bg: 'bg-blue-500/20'
  },
  {
    appName: 'weather',
    name: 'Live Weather',
    category: 'Applications',
    description: 'Real-time OpenWeather conditions, 5-day forecasts, and city search',
    icon: 'fas fa-cloud-sun',
    color: 'text-sky-400',
    bg: 'bg-sky-500/20'
  },
  {
    appName: 'colorpicker',
    name: 'Color Picker',
    category: 'Applications',
    description: 'Glass color palette generator with hex codes and CSS tokens',
    icon: 'fas fa-palette',
    color: 'text-rose-400',
    bg: 'bg-rose-500/20'
  },
  {
    appName: 'news',
    name: 'Developer News',
    category: 'Applications',
    description: 'Live Hacker News feed with category filters and search',
    icon: 'fas fa-newspaper',
    color: 'text-amber-400',
    bg: 'bg-amber-500/20'
  },
  {
    appName: 'spotify',
    name: 'Spotify Player',
    category: 'Applications',
    description: 'Curated playlists and ambient tracks embedded for coding sessions',
    icon: 'fab fa-spotify',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/20'
  },
  {
    appName: 'calculator',
    name: 'Calculator',
    category: 'Applications',
    description: 'Desktop calculator with standard mathematical operations',
    icon: 'fas fa-calculator',
    color: 'text-orange-400',
    bg: 'bg-orange-500/20'
  },
  {
    appName: 'notepad',
    name: 'Notepad',
    category: 'Applications',
    description: 'Text editor with file management and localStorage persistence',
    icon: 'fas fa-file-alt',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/20'
  },
  {
    appName: 'photos',
    name: 'Photo Viewer',
    category: 'Applications',
    description: 'Image gallery with zoom, dimensions, and metadata display',
    icon: 'fas fa-images',
    color: 'text-pink-400',
    bg: 'bg-pink-500/20'
  },
  {
    appName: 'calendar',
    name: 'Calendar & History',
    category: 'Applications',
    description: 'Interactive calendar with historical on-this-day events from Wikipedia',
    icon: 'fas fa-calendar-day',
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/20'
  }
]

const categories = ['All', 'Applications', 'Games', 'AI Tools']

const StoreApp = ({ onOpenApp }) => {
  const [activeCategory, setActiveCategory] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')

  const filteredApps = catalogApps.filter(app => {
    const matchesCategory = activeCategory === 'All' || app.category === activeCategory
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.description.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <div className="flex flex-col h-full bg-slate-950/40 p-4 sm:p-6 text-white overflow-hidden">
      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-white/10">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <i className="fas fa-shopping-bag text-pink-400"></i>
            Pink Pixel Catalog
          </h2>
          <p className="text-xs text-white/60 mt-0.5">
            Discover and launch system applications, tools, and games
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-48">
            <input
              type="text"
              placeholder="Search catalog..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/10 rounded-full px-3 py-1.5 text-xs text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-pink-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white text-xs"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex gap-1 bg-white/5 p-1 rounded-full border border-white/10">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-pink-500/30 text-pink-200 border border-pink-400/40'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid of Catalog Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 overflow-y-auto py-4 pr-1">
        {filteredApps.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center text-white/50">
            <i className="fas fa-search text-3xl mb-3"></i>
            <p className="text-sm">No applications found matching your criteria.</p>
          </div>
        ) : (
          filteredApps.map((app) => (
            <div
              key={app.appName}
              className="group bg-white/5 hover:bg-white/10 rounded-xl p-4 border border-white/10 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-xl ${app.bg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${app.icon} ${app.color} text-xl`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm text-white truncate group-hover:text-pink-300 transition-colors">
                      {app.name}
                    </h3>
                    <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 mt-1">
                      {app.category}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-white/70 leading-relaxed mb-4">
                  {app.description}
                </p>
              </div>

              <button
                onClick={() => onOpenApp?.(app.appName)}
                className="w-full py-1.5 px-3 bg-white/10 hover:bg-pink-500/20 border border-white/10 hover:border-pink-400/30 rounded-lg text-xs font-medium text-white transition-colors flex items-center justify-center gap-2"
              >
                <i className="fas fa-play text-[10px] text-pink-400"></i>
                Launch App
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default StoreApp